import { useEffect, useRef, useState } from 'react';
import { Plus, Star, X } from '@phosphor-icons/react';
import { useStore } from '../state/store';
import { ensureNotificationPermission } from '../alerts';
import type { WatchEntry } from '../types';

const HEX24 = /^[0-9a-f]{6}$/i;

/**
 * Small watchlist popover (add + list + remove), following the same
 * trigger + `.header-popover` pattern as MapToolbar/FilterPanel/SearchBox.
 */
export default function WatchlistPanel() {
  const watchlist = useStore((s) => s.watchlist);
  const addWatch = useStore((s) => s.addWatch);
  const removeWatch = useStore((s) => s.removeWatch);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    // Browser Notification permission is requested lazily, only on this
    // user gesture, never on page load.
    ensureNotificationPermission();
    const kind: WatchEntry['kind'] = HEX24.test(v) ? 'icao24' : 'callsign';
    addWatch(kind, v);
    setValue('');
  };

  const watching = watchlist.length > 0;

  return (
    <div className="watchlist-toolbar" ref={rootRef}>
      <button
        type="button"
        className={`tool watchlist-trigger${watching ? ' is-set' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        title="Watched flights: get an alert when they appear, land or depart"
      >
        <Star size={16} weight={watching ? 'fill' : 'bold'} aria-hidden="true" />
        <span className="tool-label">Watchlist</span>
        {watching && <span className="tool-count mono">{watchlist.length}</span>}
      </button>
      {open && (
        <div className="header-popover watchlist-menu glass" role="menu">
          <div className="popover-head">
            <span className="popover-title">Watchlist</span>
          </div>
          <form className="watchlist-add" onSubmit={submit}>
            <input
              className="input mono"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="UAL, BAW12 or a hex code"
              aria-label="Callsign prefix or ICAO24 hex to watch"
              autoComplete="off"
              spellCheck={false}
            />
            <button type="submit" className="icon-btn add-btn" aria-label="Add to watchlist" disabled={!value.trim()}>
              <Plus size={16} weight="bold" />
            </button>
          </form>
          {watching ? (
            <ul className="watchlist-items">
              {watchlist.map((w) => (
                <li key={w.id}>
                  <span className="watchlist-value mono">{w.value}</span>
                  <span className="watchlist-kind">{w.kind === 'icao24' ? 'Hex' : 'Prefix'}</span>
                  <button
                    type="button"
                    className="icon-btn sm watchlist-remove"
                    onClick={() => removeWatch(w.id)}
                    aria-label={`Remove ${w.value} from watchlist`}
                  >
                    <X size={13} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="popover-empty">
              Nothing watched yet. Add an airline prefix like <span className="mono">UAL</span>, a full callsign, or a
              six-character hex code, and you'll get an alert when it shows up.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
